import React, { createContext, useContext, useMemo } from 'react';
import { FontManager } from '@builder4/shared/fonts/manager';
import { IconManager } from '@builder4/shared/icons/manager';
import type { StylesConfig } from '@builder4/shared/types/styles';
import { defaultStyleConfig } from '@builder4/shared/styles/defaultStyle';
import { createButtonVariants } from '@builder4/shared/styles/buttonVariants';
import { createTypographyVariants } from '@builder4/shared/styles/typographyVariants';
import { validateStylesConfig } from '@builder4/shared/dto/styles';
import type { ValidationResult } from '@builder4/shared/utils/validators';

export type RenderErrorHandler = (result: ValidationResult) => void;

export interface FormHostConfig {
  stargateUrl: string;
  optinBackupUrl: string;
  defaultThankYouUrl: string;
}

export interface RenderContextType {
  environment: 'server' | 'builder' | 'preview' | 'inspiration-preview';
  isEditable: boolean;
  fontManager?: FontManager;
  iconManager?: IconManager;
  styles?: StylesConfig; // if not provided, default styles data is used
  onError?: RenderErrorHandler;
  formHosts?: FormHostConfig; // Form submission host configuration
}

export type RenderContextTypeStrict = Required<
  Omit<RenderContextType, 'onError' | 'formHosts' | 'iconManager'>
> & {
  iconManager: IconManager;
  buttonVariants: ReturnType<typeof createButtonVariants>;
  textVariants: ReturnType<typeof createTypographyVariants>;
  formHosts?: FormHostConfig;
};

export interface RenderProviderProps {
  value: RenderContextType;
  children: React.ReactNode;
}

const RenderContext = createContext<RenderContextTypeStrict | null>(null);

const validStyleOrNull = (
  styles?: StylesConfig,
  onError?: RenderErrorHandler,
): StylesConfig | null => {
  if (!styles) return null;

  const validationResult = validateStylesConfig(styles);
  if (!validationResult.isValid) {
    onError?.(validationResult);
    return defaultStyleConfig;
  }

  return styles;
};

export const RenderProvider: React.FC<RenderProviderProps> = ({ value, children }) => {
  const {
    styles: providedStyles,
    fontManager: providedFontManager,
    iconManager: providedIconManager,
    environment,
    onError,
  } = value;
  const fontManager: FontManager = providedFontManager ?? new FontManager();
  const iconManager: IconManager = providedIconManager ?? new IconManager();

  // Validate styles once and reuse the result
  const validatedStyles = useMemo(() => {
    return validStyleOrNull(providedStyles, onError) || defaultStyleConfig;
  }, [providedStyles, onError]);

  const buttonVariants = useMemo(() => {
    return createButtonVariants(validatedStyles, fontManager);
  }, [validatedStyles, fontManager]);

  const textVariants = useMemo(() => {
    return createTypographyVariants(validatedStyles, fontManager, environment === 'server');
  }, [validatedStyles, fontManager, environment]);

  const contextValue: RenderContextTypeStrict = {
    ...value,
    styles: validatedStyles,
    buttonVariants,
    fontManager,
    iconManager,
    textVariants,
  };

  return <RenderContext.Provider value={contextValue}>{children}</RenderContext.Provider>;
};

export const useRenderContext = (): RenderContextTypeStrict => {
  const context = useContext(RenderContext);
  if (!context) {
    throw new Error('useRenderContext must be used within a RenderProvider');
  }
  return context;
};

export { RenderContext };
